import { useNavigate } from 'react-router-dom'
import { BarChart3, TrendingUp, TrendingDown, Activity, DollarSign } from 'lucide-react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { useInventoryStore } from '../store/inventoryStore'

export default function SupplyReportsPage() {
  const navigate = useNavigate()
  const products = useInventoryStore((state) => state.products)
  const suppliers = useInventoryStore((state) => state.suppliers)
  const purchaseOrders = useInventoryStore((state) => state.purchaseOrders)

  // Inventory Turnover Data
  const inventoryTurnoverData = [
    { name: 'Jan', turnover: 4.2 },
    { name: 'Feb', turnover: 4.8 },
    { name: 'Mar', turnover: 5.1 },
    { name: 'Apr', turnover: 4.6 },
    { name: 'May', turnover: 5.3 },
    { name: 'Jun', turnover: 5.7 }
  ]

  // Supplier Performance Data
  const supplierPerformanceData = suppliers.map(s => ({
    name: s.name.substring(0, 12),
    rating: s.rating,
    onTime: s.onTime,
    quality: s.quality
  }))

  // Category Distribution
  const categoryData = [
    { name: 'Components', value: 1200, color: '#3b82f6' },
    { name: 'Connectors', value: 800, color: '#06b6d4' },
    { name: 'Power', value: 600, color: '#f59e0b' },
    { name: 'Controls', value: 247, color: '#8b5cf6' },
    { name: 'Display', value: 200, color: '#10b981' }
  ]

  // Purchase Order Trend
  const poTrendData = [
    { name: 'Week 1', orders: 12, value: 15000 },
    { name: 'Week 2', orders: 18, value: 22000 },
    { name: 'Week 3', orders: 15, value: 18500 },
    { name: 'Week 4', orders: 21, value: 26000 }
  ]

  const totalInventoryValue = products.reduce((sum, p) => sum + (p.quantity * p.unitPrice), 0)
  const avgProductValue = products.length > 0 ? totalInventoryValue / products.length : 0
  const lowStockPercentage = (products.filter(p => p.status === 'low_stock').length / products.length * 100).toFixed(1)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
          <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-amber-400" />
            <span>Supply Chain Reports</span>
          </h1>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-semibold">Total Inventory Value</p>
                <p className="text-green-400 text-2xl font-black mt-2">${(totalInventoryValue / 1000).toFixed(1)}K</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-semibold">Avg Product Value</p>
                <p className="text-cyan-400 text-2xl font-black mt-2">${avgProductValue.toFixed(2)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-cyan-400" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-semibold">Low Stock %</p>
                <p className="text-yellow-400 text-2xl font-black mt-2">{lowStockPercentage}%</p>
              </div>
              <Activity className="w-8 h-8 text-yellow-400" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-semibold">Pending Orders</p>
                <p className="text-pink-400 text-2xl font-black mt-2">{purchaseOrders.filter(o => o.status === 'pending').length}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-pink-400" />
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inventory Turnover */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4">Inventory Turnover Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={inventoryTurnoverData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line type="monotone" dataKey="turnover" stroke="#0284c7" strokeWidth={2} dot={{ fill: '#0284c7' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Purchase Order Trend */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4">Purchase Orders Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={poTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="orders" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Supplier Performance */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4">Supplier Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={supplierPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="rating" fill="#06b6d4" />
                <Bar dataKey="onTime" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Distribution */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="text-lg font-bold text-white mb-4">Inventory by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }}
                  labelStyle={{ color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Table */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <h3 className="text-lg font-bold text-white mb-4">Performance Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Metric</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Current Month</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Last Month</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Change</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-blue-500/10 hover:bg-blue-500/5">
                  <td className="py-3 px-4 text-white">Inventory Turnover Rate</td>
                  <td className="py-3 px-4 text-green-400 font-semibold">5.7</td>
                  <td className="py-3 px-4 text-slate-300">5.3</td>
                  <td className="py-3 px-4 text-green-400">+7.5%</td>
                </tr>
                <tr className="border-b border-blue-500/10 hover:bg-blue-500/5">
                  <td className="py-3 px-4 text-white">Supplier On-Time Delivery</td>
                  <td className="py-3 px-4 text-green-400 font-semibold">94%</td>
                  <td className="py-3 px-4 text-slate-300">91%</td>
                  <td className="py-3 px-4 text-green-400">+3.3%</td>
                </tr>
                <tr className="border-b border-blue-500/10 hover:bg-blue-500/5">
                  <td className="py-3 px-4 text-white">Stock-Out Rate</td>
                  <td className="py-3 px-4 text-red-400 font-semibold">0.3%</td>
                  <td className="py-3 px-4 text-slate-300">0.5%</td>
                  <td className="py-3 px-4 text-green-400">-40%</td>
                </tr>
                <tr className="hover:bg-blue-500/5">
                  <td className="py-3 px-4 text-white">Average Lead Time (days)</td>
                  <td className="py-3 px-4 text-yellow-400 font-semibold">7.2</td>
                  <td className="py-3 px-4 text-slate-300">8.1</td>
                  <td className="py-3 px-4 text-green-400">-11%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
